// src/pages/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import { login } from '../utils/api';
import { setAuthToken, setBrotherData } from '../utils/auth';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(420px, .95fr);
  background: ${({ theme }) => theme.colors.canvas};
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Story = styled.section`
  background: #232a24;
  color: #f7f5ec;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  padding: clamp(32px, 6vw, 80px);
  @media (max-width: 820px) { min-height: 300px; padding: 32px 24px; }
`;
const Brand = styled.div`
  align-items: center; display: flex; font-family: ${({ theme }) => theme.typography.display};
  font-size: 22px; gap: 10px;
  &::before { background: #d7bb74; border-radius: 50%; content: ''; height: 10px; width: 10px; }
`;
const StoryCopy = styled.div`max-width: 620px;`;
const Eyebrow = styled.p`color: #d7bb74; font-size: 12px; font-weight: 700; letter-spacing: .16em; text-transform: uppercase;`;
const Headline = styled.h1`
  font-family: ${({ theme }) => theme.typography.display}; font-size: clamp(42px, 6vw, 76px);
  font-weight: 400; letter-spacing: -.035em; line-height: .98; margin: 18px 0;
`;
const StoryText = styled.p`color: rgba(247,245,236,.7); font-size: 16px; line-height: 1.65; max-width: 500px;`;
const FormSide = styled.main`
  align-items: center; display: flex; justify-content: center; padding: clamp(28px, 6vw, 72px) 24px;
`;
const LoginForm = styled.form`
  width: 100%;
  max-width: 420px;
`;
const FormTitle = styled.h2`font-family: ${({ theme }) => theme.typography.display}; font-size: 36px; font-weight: 400; margin: 0 0 8px;`;
const FormIntro = styled.p`color: ${({ theme }) => theme.colors.textMuted}; line-height: 1.5; margin: 0 0 30px;`;
const Field = styled.div`margin-top: 18px;`;
const Label = styled.label`display: block; font-size: 13px; font-weight: 650; margin-bottom: 7px;`;
const Input = styled.input`
  width: 100%;
  padding: 12px 13px;
  border: 1px solid ${({ theme, $invalid }) => $invalid ? theme.colors.danger : theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  &:focus { border-color: ${({ theme }) => theme.colors.accent}; outline: 3px solid ${({ theme }) => theme.colors.accentSoft}; }
`;
const Button = styled.button`
  align-items: center; background: ${({ theme }) => theme.colors.text}; border: 0;
  border-radius: ${({ theme }) => theme.radius.sm}; color: white; cursor: pointer;
  display: flex; font-weight: 650; justify-content: space-between; margin-top: 24px;
  padding: 12px 14px; width: 100%;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.accent}; }
  &:disabled { cursor: wait; opacity: .65; }
`;
const ErrorMessage = styled.p`
  background: #fff0ef; border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger}; margin: 18px 0 0; padding: 10px 12px;
`;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await login(formData.email, formData.password);
      setAuthToken(response.data.token);
      setBrotherData(response.data.brother);
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed. Check your email and password, then try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Story aria-label="About Rushtracker">
        <Brand>Rushtracker</Brand>
        <StoryCopy>
          <Eyebrow>Your chapter, in focus</Eyebrow>
          <Headline>Make every conversation count.</Headline>
          <StoryText>One considered workspace for events, rushees, notes, and the decisions that shape your next class.</StoryText>
        </StoryCopy>
      </Story>
      <FormSide>
        <LoginForm onSubmit={handleSubmit} noValidate>
          <FormTitle>Welcome back</FormTitle>
          <FormIntro>Sign in to continue to your chapter workspace.</FormIntro>
          <Field>
            <Label htmlFor="email">Email address</Label>
            <Input id="email" name="email" type="email" autoComplete="email" required
              $invalid={Boolean(error)} value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </Field>
          <Field>
            <Label htmlFor="password">Password</Label>
            <Input id="password" name="password" type="password" autoComplete="current-password" required
              $invalid={Boolean(error)} value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
          </Field>
          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            <span>{isLoading ? 'Signing in…' : 'Sign in'}</span><ArrowRight size={17} aria-hidden="true" />
          </Button>
        </LoginForm>
      </FormSide>
    </LoginContainer>
  );
};

export default Login;